/**
 * components/profile/ProfileUnderstandsCard.jsx — v3.7.8 Phase 84.31 Part 2
 *
 * The "What 2120 understands" card. Renders the real fact list returned
 * by GET /api/profile/summary — never fabricates rows. Each fact shows:
 *
 *   • Label
 *   • Human-readable value (with "Prefer not to say" and "Not set"
 *     handled explicitly)
 *   • A status chip: Confirmed · Inferred · Needs confirmation
 *   • A tiny "Why we ask" + "Where this is used" affordance on click
 *   • A deep-link back to the question inside the current Profile hub
 *     (either the Life tab or the Location tab, per the fact's `tab`).
 *
 * Loading / empty / error states are handled explicitly — a missing
 * fetch never leaves the card in a permanent skeleton state.
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, HelpCircle, Sparkles, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_META = {
  confirmed: {
    label: 'Confirmed',
    icon:  CheckCircle2,
    cls:   'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900',
  },
  inferred: {
    label: 'Inferred',
    icon:  Sparkles,
    cls:   'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900',
  },
  needs_confirmation: {
    label: 'Needs confirmation',
    icon:  HelpCircle,
    cls:   'bg-muted text-muted-foreground border-border',
  },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.needs_confirmation;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${meta.cls}`}
      data-testid={`profile-summary-status-${status}`}
      role="status"
      aria-label={`Status: ${meta.label}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      <span aria-hidden="true">{meta.label}</span>
    </span>
  );
}

function FactRow({ fact, onDeepLink }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      className="py-3 border-t border-border/60 first:border-t-0"
      data-testid={`profile-summary-fact-${fact.key}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-start justify-between gap-3 text-left group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
        aria-expanded={expanded}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-charcoal">{fact.label}</span>
            <StatusChip status={fact.status} />
          </div>
          <div className="text-sm text-muted-foreground mt-0.5 truncate">
            {fact.value_pretty}
          </div>
          {fact.last_confirmed_at && (
            <div className="text-[11px] text-muted-foreground/70 mt-0.5">
              Last confirmed {fact.last_confirmed_at}
            </div>
          )}
        </div>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground shrink-0 mt-1 transition-transform ${expanded ? 'rotate-90' : ''}`}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div
          className="mt-2 pl-1 space-y-1.5 text-[12px] leading-relaxed text-muted-foreground"
          data-testid={`profile-summary-fact-${fact.key}-detail`}
        >
          <p><span className="font-medium text-charcoal">Why we ask.</span> {fact.why}</p>
          <p><span className="font-medium text-charcoal">Where this is used.</span> {fact.used}</p>
          <div className="pt-1">
            <button
              type="button"
              onClick={() => onDeepLink(fact)}
              className="text-primary text-[12px] hover:underline"
              data-testid={`profile-summary-fact-${fact.key}-review`}
            >
              {fact.status === 'confirmed' ? 'Review or update' : 'Add or confirm'} →
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function ProfileUnderstandsCard({ summary, loading, error }) {
  const [, setSearchParams] = useSearchParams();

  const onDeepLink = (fact) => {
    // Deep-link within the Profile hub. Location facts route to
    // ?tab=location; everything else stays on the Life tab (this
    // component's home) — we scroll to the question form below.
    if (fact.tab === 'location') {
      setSearchParams({ tab: 'location' }, { replace: false });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Focus the target field inside the LifeProfileEmbed catalog.
    const el = document.querySelector(`[data-lp-field="${fact.key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { el.focus({ preventScroll: true }); } catch { /* non-focusable is fine */ }
    } else {
      // Fallback — just scroll a bit further down where the form lives.
      window.scrollTo({ top: window.scrollY + 400, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="profile-summary-loading">
        <CardContent className="p-5 space-y-3">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border-border/70" data-testid="profile-summary-error">
        <CardContent className="p-5 text-sm text-muted-foreground">
          Couldn&rsquo;t load your profile summary right now. Please refresh in a minute.
        </CardContent>
      </Card>
    );
  }

  const facts = (summary?.facts || []);
  const confirmed = facts.filter((f) => f.status === 'confirmed').length;

  return (
    <Card className="rounded-2xl border-border/70" data-testid="profile-summary-card">
      <CardContent className="p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h3 className="font-display text-lg font-medium text-charcoal">
            What 2120 understands
          </h3>
          <Badge variant="outline" className="rounded-full text-[10px]" data-testid="profile-summary-count">
            {confirmed} confirmed · {facts.length - confirmed} to review
          </Badge>
        </div>
        <p className="text-[12px] text-muted-foreground mt-1 leading-relaxed">
          These are the facts 2120 currently uses to personalise your plan. Tap any row for the reasoning
          and to update or confirm it.
        </p>
        <ul className="mt-3">
          {facts.map((f) => (
            <FactRow key={f.key} fact={f} onDeepLink={onDeepLink} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
